/**
 * DataMap layout: everything the picture needs that depends only on the map's data,
 * computed once per data change (useMemo on the payload). Pure: no DOM, no React.
 *
 *   tables      the ring, grouped by area (domain), each with its angle
 *   arcs        one coloured arc per area, with the path its label runs along
 *   hubs        the shared keys, placed inside the ring near the tables that carry them
 *   members     one curve per (table, hub): the table carries that key
 *   pairs       direct links (no shared key), one chord per table pair
 *   verified    verified links, one curve per table pair, drawn in deep amber
 *
 * Visibility under the link filter and focus is decided elsewhere (visibility()), so a
 * filter change never moves anything.
 */

export const R = 380
const GAP = 3
const MAX_HUBS = 40

// The validated palette, one colour per area.
export const DOMAIN_COLORS = {
  people: '#2F6FD0',
  payroll: '#D9572B',
  benefits: '#0E9384',
  time: '#5B6B8C',
  company: '#A07C0E',
  lookups: '#D44C8C',
  recruiting: '#3A8F2E',
  onboarding: '#7C6BE0',
  access: '#D64545',
  other: '#8A8378',
}
export const AMBER = '#B45309'
export const INK = '#1C1B18'
export const MUTED = '#66615A'
export const FAINT = '#A29C91'
export const HOLLOW = '#C9C3B7'

export function pt(a, r) {
  return [Math.sin(a) * r, -Math.cos(a) * r]
}

function fmtPt(p) {
  return `${p[0].toFixed(1)},${p[1].toFixed(1)}`
}

export function pairKey(a, b) {
  return a < b ? `${a}\u0000${b}` : `${b}\u0000${a}`
}

function arcPath(r0, r1, a0, a1) {
  // a thin annular sector from angle a0 to a1 (radians, clockwise from 12 o'clock)
  const large = a1 - a0 > Math.PI ? 1 : 0
  const p0 = pt(a0, r1), p1 = pt(a1, r1), p2 = pt(a1, r0), p3 = pt(a0, r0)
  return `M${fmtPt(p0)}A${r1},${r1} 0 ${large} 1 ${fmtPt(p1)}L${fmtPt(p2)}A${r0},${r0} 0 ${large} 0 ${fmtPt(p3)}Z`
}

export function computeLayout(data) {
  const domainOrder = (data?.domains || []).map(d => d.id)
  const domainLabel = Object.fromEntries((data?.domains || []).map(d => [d.id, d.label]))
  const rank = id => {
    const i = domainOrder.indexOf(id)
    return i < 0 ? domainOrder.length : i
  }
  const tables = (data?.tables || [])
    .map(t => ({ ...t }))
    .sort((x, y) => rank(x.domain) - rank(y.domain)
      || (x.linked === y.linked ? 0 : x.linked ? -1 : 1)
      || x.id.localeCompare(y.id))
  const byId = new Map(tables.map(t => [t.id, t]))

  const present = [...new Set(tables.map(t => t.domain))]
  const slots = Math.max(1, tables.length + present.length * GAP)
  const step = (2 * Math.PI) / slots
  let k = 0
  let prev = null
  tables.forEach(t => {
    if (prev !== null && t.domain !== prev) k += GAP
    t.a = (k + GAP / 2) * step
    t.p = pt(t.a, R - 6)
    k += 1
    prev = t.domain
  })

  const arcs = present.map((d, i) => {
    const a = domainLabel[d] || d
    const ts = tables.filter(t => t.domain === d)
    const a0 = ts[0].a - step * 0.6
    const a1 = ts[ts.length - 1].a + step * 0.6
    const mid = (a0 + a1) / 2
    const low = mid > Math.PI / 2 && mid < (3 * Math.PI) / 2
    const e0 = a0 - step * 2, e1 = a1 + step * 2
    const rr = low ? R + 27 : R + 17
    const P0 = pt(low ? e1 : e0, rr), P1 = pt(low ? e0 : e1, rr)
    const sweep = Math.abs(e1 - e0) > Math.PI ? 1 : 0
    // the label runs along the arc plus the gap either side; shrink it to fit, else leave
    // it to the legend
    const room = (a1 - a0 + step * GAP * 0.6) * rr
    const want = a.length * 10.5
    const fontSize = want <= room ? 12 : Math.max(8, (12 * room) / want)
    const fits = a.length * 10.5 * (fontSize / 12) <= room
    return {
      id: d,
      key: `dmap-arc-${i}`,
      fontSize,
      fits,
      label: a,
      color: DOMAIN_COLORS[d] || DOMAIN_COLORS.other,
      d: arcPath(R + 3, R + 8, a0, a1),
      textPath: `M${fmtPt(P0)}A${rr},${rr} 0 ${sweep} ${low ? 0 : 1} ${fmtPt(P1)}`,
      count: ts.length,
      span: a1 - a0,
    }
  })

  // ── hubs: the most-shared keys, each at the mean angle of its tables ──
  const rawHubs = (data?.hubs || [])
    .map(h => ({ ...h, members: (h.members || []).filter(m => byId.has(m.t)) }))
    .filter(h => h.members.length >= 2)
  const shown = rawHubs.slice(0, MAX_HUBS)
  const shownIds = new Set(shown.map(h => h.id))
  const hubs = shown.map(h => {
    let sx = 0, sy = 0
    h.members.forEach(m => { const a = byId.get(m.t).a; sx += Math.sin(a); sy += Math.cos(a) })
    return { ...h, n: h.members.length, a: (Math.atan2(sx, sy) + 2 * Math.PI) % (2 * Math.PI) }
  }).sort((x, y) => x.a - y.a)
  hubs.forEach((h, i) => {
    h.r = [250, 180, 115][i % 3]
    h.rad = 9 + Math.sqrt(h.n) * 3.1
    const p = pt(h.a, h.r)
    h.x = p[0]; h.y = p[1]
  })
  for (let pass = 0; pass < 320; pass++) {
    for (let i = 0; i < hubs.length; i++) {
      for (let j = i + 1; j < hubs.length; j++) {
        const A = hubs[i], B = hubs[j]
        const dx = B.x - A.x, dy = B.y - A.y
        const dist = Math.sqrt(dx * dx + dy * dy) || 0.01
        const need = A.rad + B.rad + 36
        if (dist < need) {
          const mv = (need - dist) / 2, ux = dx / dist, uy = dy / dist
          A.x -= ux * mv; A.y -= uy * mv; B.x += ux * mv; B.y += uy * mv
        }
      }
    }
    hubs.forEach(h => {
      const rr = Math.sqrt(h.x * h.x + h.y * h.y)
      const lim = R - 95
      if (rr > lim) { h.x *= lim / rr; h.y *= lim / rr }
      const clear = 100 + h.rad
      if (rr < clear) { const s = clear / (rr || 1); h.x *= s; h.y *= s }
    })
  }
  hubs.forEach(h => { h.r = Math.sqrt(h.x * h.x + h.y * h.y) })
  const hubById = new Map(hubs.map(h => [h.id, h]))

  const curve = (t, h) => {
    const c = pt(t.a, (R + h.r) / 2 - 40)
    return `M${fmtPt(t.p)}Q${fmtPt(c)} ${h.x.toFixed(1)},${h.y.toFixed(1)}`
  }
  const chord = (ta, tb) => {
    let d = Math.abs(ta.a - tb.a)
    if (d > Math.PI) d = 2 * Math.PI - d
    const mid = Math.atan2(Math.sin(ta.a) + Math.sin(tb.a), Math.cos(ta.a) + Math.cos(tb.a))
    const c = pt(mid, (R - 6) * Math.max(0, 1 - d / Math.PI) * 0.78)
    return `M${fmtPt(ta.p)}Q${fmtPt(c)} ${fmtPt(tb.p)}`
  }
  const verPath = (ta, tb) => {
    const mid = Math.atan2(Math.sin(ta.a) + Math.sin(tb.a), Math.cos(ta.a) + Math.cos(tb.a))
    return `M${fmtPt(ta.p)}Q${fmtPt(pt(mid, 60))} ${fmtPt(tb.p)}`
  }

  const members = []
  hubs.forEach(h => {
    h.members.forEach(m => {
      const t = byId.get(m.t)
      members.push({ key: `${h.id}\u0000${m.t}`, t: m.t, h: h.id, cols: m.cols || [], kinds: m.kinds || [], d: curve(t, h) })
    })
  })

  // links through a key that is not drawn as a hub become direct links
  const pairs = new Map()
  const verified = new Map()
  ;(data?.links || []).forEach(l => {
    const ta = byId.get(l.a), tb = byId.get(l.b)
    if (!ta || !tb) return
    if (!l.hub || !shownIds.has(l.hub)) {
      const key = pairKey(l.a, l.b)
      let p = pairs.get(key)
      if (!p) {
        p = { key, a: l.a, b: l.b, cols: [], kinds: [], d: chord(ta, tb) }
        pairs.set(key, p)
      }
      p.cols.push({ a: l.a, ac: l.ac, bc: l.bc, kind: l.kind, method: l.method })
      if (!p.kinds.includes(l.kind)) p.kinds.push(l.kind)
    }
    if (l.kind === 'verified') {
      const key = pairKey(l.a, l.b)
      let v = verified.get(key)
      if (!v) {
        v = { key, a: l.a, b: l.b, cols: [], d: verPath(ta, tb) }
        verified.set(key, v)
      }
      v.cols.push({ a: l.a, ac: l.ac, bc: l.bc, method: l.method })
    }
  })

  const labelSize = Math.max(6.5, Math.min(9.5, ((2 * Math.PI * (R + 34)) / slots) * 0.82))

  return {
    tables, byId, arcs, hubs, hubById, members,
    pairs: [...pairs.values()], verified: [...verified.values()],
    settings: data?.settings_tables || [],
    step, labelSize, curve, chord, verPath,
  }
}

/**
 * What is on under a link filter ({verified, matched}): which memberships, hubs, direct
 * pairs and verified curves show, which tables count as connected, and the adjacency the
 * route finder walks. Cheap; recomputed when the filter changes.
 */
export function visibility(layout, filter) {
  const on = kinds => kinds.some(k => (k === 'verified' ? filter.verified : filter.matched))
  const memberOn = new Set()
  const hubCount = new Map()
  layout.members.forEach(m => {
    if (on(m.kinds)) {
      memberOn.add(m.key)
      hubCount.set(m.h, (hubCount.get(m.h) || 0) + 1)
    }
  })
  const hubOn = new Set([...hubCount].filter(([, n]) => n >= 2).map(([h]) => h))
  const pairOn = new Set(layout.pairs.filter(p => on(p.kinds)).map(p => p.key))
  const verOn = filter.verified

  const adj = new Map()
  const add = (x, y, edge) => {
    if (!adj.has(x)) adj.set(x, [])
    if (!adj.has(y)) adj.set(y, [])
    adj.get(x).push([y, edge])
    adj.get(y).push([x, edge])
  }
  const connected = new Set()
  layout.members.forEach(m => {
    if (memberOn.has(m.key) && hubOn.has(m.h)) {
      add(m.t, `hub:${m.h}`, { m })
      connected.add(m.t)
    }
  })
  layout.pairs.forEach(p => {
    if (pairOn.has(p.key)) {
      add(p.a, p.b, { p })
      connected.add(p.a); connected.add(p.b)
    }
  })
  if (verOn) {
    layout.verified.forEach(v => {
      add(v.a, v.b, { v })
      connected.add(v.a); connected.add(v.b)
    })
  }
  return { memberOn, hubOn, hubCount, pairOn, verOn, adj, connected }
}

/** Tables one step from `id` (through a shared key, a direct link or a verified link). */
export function reachOf(vis, id) {
  const reach = new Set([id])
  ;(vis.adj.get(id) || []).forEach(([y]) => {
    if (y.startsWith('hub:')) {
      (vis.adj.get(y) || []).forEach(([z]) => reach.add(z))
    } else {
      reach.add(y)
    }
  })
  return reach
}

/** Shortest route from table `a` to table `b` over what the filter shows: [nodes, edges]. */
export function findRoute(vis, a, b) {
  if (!a || !b || a === b) return null
  const prevN = new Map(), prevE = new Map()
  const seen = new Set([a])
  const q = [a]
  while (q.length) {
    const x = q.shift()
    if (x === b) break
    for (const [y, e] of vis.adj.get(x) || []) {
      if (!seen.has(y)) { seen.add(y); prevN.set(y, x); prevE.set(y, e); q.push(y) }
    }
  }
  if (!seen.has(b)) return null
  const nodes = [b]
  while (nodes[0] !== a) nodes.unshift(prevN.get(nodes[0]))
  return { nodes, edges: nodes.slice(1).map(n => prevE.get(n)) }
}

/** A quadratic path drawn the other way round (so a route animates from its start). */
export function reversePath(d) {
  const m = d.match(/^M([^Q]+)Q(\S+) (.+)$/)
  return m ? `M${m[3]}Q${m[2]} ${m[1]}` : d
}
